import { useEffect, useState } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_SECTIONS } from "../content";
import { markNavJump } from "../motion";
import { Logo } from "./Section";

const SECTION_ALIAS = {
  understanding: "vision",
  audience: "experience",
  journeys: "concept",
  architecture: "concept",
  brand: "concept",
  partnership: "investment",
  trust: "investment",
  why: "investment",
};

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  markNavJump();
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Nav({ onDiscuss, onOverview }) {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("vision");
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 24, mass: 0.4 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = [...NAV_SECTIONS.map((s) => s.id), ...Object.keys(SECTION_ALIAS)];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(SECTION_ALIAS[e.target.id] || e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "discuss") {
      onDiscuss();
      return;
    }
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-[80] transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled ? "bg-[#242424]/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 xl:px-12 h-16 flex items-center justify-between gap-4 xl:gap-6">
          <button
            data-testid="nav-logo"
            onClick={() => go("vision")}
            className="shrink-0 cursor-pointer"
            aria-label="Back to the beginning"
          >
            <Logo dark />
          </button>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-7" aria-label="Proposal sections">
            {NAV_SECTIONS.slice(0, 6).map(({ id, label }, i) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className={`group relative text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 cursor-pointer ${
                  active === id ? "text-[#FFD400]" : "text-[#F5F3EE]/65 hover:text-[#F5F3EE]"
                }`}
              >
                <span className="mr-1.5 text-[9px] opacity-50">0{i + 1}</span>
                {label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-px bg-[#FFD400] transition-[width] duration-400 ${
                    active === id ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-discuss-button"
              onClick={onDiscuss}
              className="hidden sm:inline-flex items-center gap-2 bg-[#FFD400] text-[#0F0F0F] text-[11px] font-bold uppercase tracking-[0.16em] px-5 py-2.5 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#E6BE00] hover:-translate-y-0.5 cursor-pointer"
            >
              Discuss the Project
              <ArrowUpRight size={13} strokeWidth={2.5} />
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-[#F5F3EE] p-2 cursor-pointer"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        <motion.div
          data-testid="scroll-progress"
          className="h-[2px] bg-[#FFD400] origin-left"
          style={{ scaleX: progress }}
        />
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed top-16 inset-x-0 z-[75] bg-[#242424] border-b border-white/10 lg:hidden"
          >
            <nav className="px-6 py-6 flex flex-col" aria-label="Mobile proposal sections">
              {NAV_SECTIONS.map(({ id, label }, i) => (
                <button
                  key={id}
                  data-testid={`mobile-nav-link-${id}`}
                  onClick={() => go(id)}
                  className={`text-left py-3.5 border-b border-white/10 text-sm font-medium tracking-wide flex items-baseline gap-4 cursor-pointer transition-colors duration-300 ${
                    active === id ? "text-[#FFD400]" : "text-[#F5F3EE]/80"
                  }`}
                >
                  <span className="text-[10px] font-semibold tracking-[0.2em] opacity-45">0{i + 1}</span>
                  {label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-overview"
                onClick={() => {
                  setOpen(false);
                  onOverview();
                }}
                className="text-left pt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F5F3EE]/50 underline underline-offset-8 decoration-[#F5F3EE]/25 transition-colors duration-300 hover:text-[#F5F3EE] cursor-pointer"
              >
                Executive overview · two minutes
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
